/**
 * Settings > Messaging > Reminders.
 *
 * Follow-up emails to customers who still owe documents, set once for every
 * claim and transfer. Claims: whether reminders go (turning them on asks
 * whether requests already open get them too), how often and how many, and
 * what happens when nothing arrives. Transfers: how often and how many; staff
 * turn them on per transfer. A setup guide shows until claims have a document
 * request to follow up.
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'
import { AlertTriangle, ArrowRight, CheckCircle2, Circle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  type ReminderSettings,
  type ReminderSettingsUpdate,
  usePreviewReminderSettings,
  useReminderSettings,
  useUpdateReminderSettings,
} from '@/admin/hooks/use-reminder-settings'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { toastApiError } from '@/shared/lib/api-error'
import { useForm } from '@/shared/lib/forms'
import type { FormContext, FormField } from '@/shared/lib/forms'

function toNullableInt(value: unknown): number | null {
  if (value === '' || value === null || value === undefined) return null
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null
}

const SECTION_LABEL =
  'mb-2 text-[11.8px] uppercase leading-[1.4] tracking-[0.1em] text-[var(--ink-3)]'
const CARD = 'rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)]'

export function RemindersSettingsPage() {
  const { t } = useTranslation()
  const { data, isPending, isError } = useReminderSettings()
  const update = useUpdateReminderSettings()
  const preview = usePreviewReminderSettings()
  const { can } = usePermissions()
  const canEdit = can('reminders.configure')
  const emailTemplatesOn = useFeatureFlag(FLAGS.EMAIL_AUTHORING)
  const emailTriggersOn = useFeatureFlag(FLAGS.EMAIL_TRIGGERS)
  // The "who gets reminders" question, asked when reminders are turned on.
  const [asking, setAsking] = useState(false)
  // Asked before a save that would close claims; resolves with the answer.
  const [closing, setClosing] = useState<{ count: number; perRun: number } | null>(null)
  const closingAnswer = useRef<((save: boolean) => void) | null>(null)
  const confirmClosing = useCallback(
    (ask: { count: number; perRun: number }) =>
      new Promise<boolean>((resolve) => {
        closingAnswer.current = resolve
        setClosing(ask)
      }),
    [],
  )
  const answerClosing = (save: boolean) => {
    closingAnswer.current?.(save)
    closingAnswer.current = null
    setClosing(null)
  }

  const claims = data?.claims
  const waiting = data?.summary.waiting ?? 0
  const transfersAvailable = Boolean(data?.setup.transfers_available)

  const claimFields = useMemo<FormField[]>(() => {
    const on = (ctx: FormContext) => Boolean(ctx.values.send_reminders)
    const closing = (ctx: FormContext) => on(ctx) && Boolean(ctx.values.auto_close)
    const reopenWindow = t('reminders.settings.months', {
      count: claims?.reopen_window_months ?? 12,
      defaultValue_one: 'a month',
      defaultValue_other: '{{count}} months',
    })
    const num = (name: string, label: string, value: number | null | undefined): FormField => ({
      type: 'number',
      name,
      label,
      value: value ?? '',
      placeholder: t('reminders.settings.defaultPlaceholder', 'Default'),
      disabled: !canEdit,
      className: 'col-span-1',
    })
    return [
      {
        type: 'switch',
        name: 'send_reminders',
        label: t('reminders.settings.send', 'Send reminders'),
        testId: 'reminders-send',
        className: 'col-span-2',
        value: claims?.send_reminders ?? false,
        disabled: !canEdit,
        describe: (ctx) => sendLine(ctx, t, claims, waiting),
        onChange: (next) => {
          if (next && !claims?.send_reminders && waiting > 0) setAsking(true)
        },
      },
      { ...num('interval_days', t('reminders.settings.daysBetween', 'Days between reminders'), claims?.interval_days), visible: on },
      { ...num('max_reminders', t('reminders.settings.maximum', 'Maximum reminders'), claims?.max_reminders), visible: on },
      {
        type: 'switch',
        name: 'auto_close',
        label: t('reminders.settings.close', 'Close the {{claimNounLower}} if nothing arrives'),
        testId: 'reminders-close',
        className: 'col-span-2',
        value: claims?.auto_close ?? false,
        disabled: !canEdit,
        visible: on,
        describe: (ctx) => closeLine(ctx, t),
      },
      {
        ...num(
          'close_after_days',
          t('reminders.settings.daysAfterLast', 'Days after the last reminder'),
          claims?.close_after_days,
        ),
        visible: closing,
      },
      {
        type: 'checkbox',
        name: 'closure_email',
        label: t(
          'reminders.settings.closureEmail',
          'Email the customer when the {{claimNounLower}} closes',
        ),
        testId: 'reminders-closure-email',
        className: 'col-span-2',
        value: claims?.closure_email ?? false,
        disabled: !canEdit,
        visible: closing,
      },
      {
        type: 'checkbox',
        name: 'reopen_on_documents',
        label: t(
          'reminders.settings.reopen',
          'Reopen the {{claimNounLower}} if every document arrives within {{window}}',
          { window: reopenWindow },
        ),
        testId: 'reminders-reopen',
        className: 'col-span-2',
        value: claims?.reopen_on_documents ?? false,
        disabled: !canEdit,
        visible: closing,
      },
      // The answer to "who gets reminders", saved with the rest.
      { type: 'checkbox', name: 'apply_to_open', value: false, visible: false },
    ]
  }, [claims, waiting, canEdit, t])

  const transferFields = useMemo<FormField[]>(
    () => [
      {
        type: 'number',
        name: 'transfers_interval_days',
        label: t('reminders.settings.daysBetween', 'Days between reminders'),
        value: data?.transfers.interval_days ?? '',
        placeholder: t('reminders.settings.defaultPlaceholder', 'Default'),
        disabled: !canEdit,
      },
      {
        type: 'number',
        name: 'transfers_max_reminders',
        label: t('reminders.settings.maximum', 'Maximum reminders'),
        value: data?.transfers.max_reminders ?? '',
        placeholder: t('reminders.settings.defaultPlaceholder', 'Default'),
        disabled: !canEdit,
      },
    ],
    [data, canEdit, t],
  )

  const transfersForm = useForm(
    {
      name: `reminder-transfers-${data ? 'ready' : 'empty'}`,
      submit: 'footer',
      fields: transferFields,
    },
    async () => undefined,
    [data],
  )

  const claimsForm = useForm(
    { name: `reminder-claims-${data ? 'ready' : 'empty'}`, submit: 'footer', fields: claimFields },
    async (raw) => {
      const v = raw as Record<string, unknown>
      const tv = transfersForm.ref.current?.values ?? {}
      const send = Boolean(v.send_reminders)
      const body: ReminderSettingsUpdate = {
        claims: {
          send_reminders: send,
          interval_days: toNullableInt(v.interval_days),
          max_reminders: toNullableInt(v.max_reminders),
          // Closing counts reminders, so it can only stay on with them.
          auto_close: send && Boolean(v.auto_close),
          close_after_days: toNullableInt(v.close_after_days),
          closure_email: Boolean(v.closure_email),
          reopen_on_documents: Boolean(v.reopen_on_documents),
          ...(send && !claims?.send_reminders
            ? { apply_to_open: Boolean(v.apply_to_open) }
            : {}),
        },
        ...(transfersAvailable
          ? {
              transfers: {
                interval_days: toNullableInt(tv.transfers_interval_days),
                max_reminders: toNullableInt(tv.transfers_max_reminders),
              },
            }
          : {}),
      }
      try {
        // Saving can close claims whose reminders have all gone out: say how
        // many first, and save only if the admin still wants to.
        if (body.claims?.auto_close) {
          const { closes_now: count, per_run: perRun } = await preview.mutateAsync(body)
          if (count > 0 && !(await confirmClosing({ count, perRun }))) return
        }
        await update.mutateAsync(body)
        toast.success(t('reminders.settings.saved', 'Reminder settings saved.'))
      } catch (err) {
        toastApiError(err, t('reminders.settings.saveFailed', 'Could not save the reminder settings'))
        throw err
      }
    },
    [update, preview, confirmClosing, data, transfersAvailable, t],
  )

  const answer = (includeOpen: boolean | null) => {
    if (includeOpen === null) claimsForm.ref.current?.setFieldValue('send_reminders', false)
    else claimsForm.ref.current?.setFieldValue('apply_to_open', includeOpen)
    setAsking(false)
  }

  const { Form: ClaimsForm, FormFooter } = claimsForm
  const TransfersForm = transfersForm.Form

  return (
    <div className="max-w-[560px] py-2">
      <h2 className="mb-1 text-[18px] font-light text-[var(--ink)]">
        {t('reminders.settings.title', 'Reminders')}
      </h2>
      <p className="mb-5 text-[13px] leading-relaxed text-[var(--ink-3)]">
        {t(
          'reminders.settings.description',
          'Follow-up emails to customers who still owe you documents. Set them up once here and they apply to every {{claimNounLower}} and transfer.',
        )}
      </p>
      {isPending ? (
        <p className="text-[13px] text-[var(--ink-3)]">
          {t('reminders.settings.loading', 'Loading settings...')}
        </p>
      ) : isError || !data ? (
        <p className="text-[13px] text-[var(--status-error)]">
          {t('reminders.settings.loadError', 'Could not load the reminder settings.')}
        </p>
      ) : (
        <>
          <div className={SECTION_LABEL}>{t('reminders.settings.claims', '{{claimNounPlural}}')}</div>
          <section aria-label={t('reminders.settings.claimsLabel', '{{claimNoun}} reminders')} className={CARD}>
            <ClaimsForm className="grid grid-cols-2 gap-5 p-5">
              {({ formContext }) => (
                <ClaimsCardFoot
                  formContext={formContext}
                  data={data}
                  emailTemplatesOn={emailTemplatesOn}
                  emailTriggersOn={emailTriggersOn}
                />
              )}
            </ClaimsForm>
          </section>

          <div className={`mt-7 ${SECTION_LABEL}`}>{t('reminders.settings.transfers', 'Transfers')}</div>
          <section
            aria-label={t('reminders.settings.transfersLabel', 'Transfer reminders')}
            className={`${CARD} p-5`}
          >
            <p className="text-[14px] font-medium leading-none text-[var(--ink)]">
              {t('reminders.settings.transfersTiming', 'Reminder timing')}
            </p>
            <p className="mt-1 text-[12px] text-[var(--ink-3)]">
              {transfersAvailable
                ? t(
                    'reminders.settings.transfersHow',
                    'Staff turn reminders on for each transfer when they ask for documents. These settings decide how often they go.',
                  )
                : t(
                    'reminders.settings.transfersUnavailable',
                    'Not available for your account. Contact support to turn transfers on.',
                  )}
            </p>
            {transfersAvailable && (
              <TransfersForm className="mt-5 grid grid-cols-2 gap-5">
                {data.emails.transfer_reminder ? (
                  <EmailsUsed
                    names={[data.emails.transfer_reminder]}
                    editable={emailTemplatesOn}
                    className="mt-5"
                  />
                ) : null}
              </TransfersForm>
            )}
          </section>

          {canEdit ? (
            FormFooter && (
              <div className="mt-6 flex justify-end gap-2.5">
                <FormFooter
                  onCancel={() => {
                    claimsForm.ref.current?.resetForm()
                    transfersForm.ref.current?.resetForm()
                  }}
                  disabled={update.isPending}
                  submitText={
                    update.isPending
                      ? t('reminders.saving', 'Saving...')
                      : t('reminders.settings.save', 'Save changes')
                  }
                />
              </div>
            )
          ) : (
            <p className="mt-6 text-[12px] text-[var(--ink-3)]">
              {t(
                'reminders.settings.readOnly',
                'You can see these settings. Changing them needs permission to edit {{claimNounPluralLower}}.',
              )}
            </p>
          )}
        </>
      )}

      {asking && (
        <Dialog open onOpenChange={(open) => !open && answer(null)}>
          <DialogContent className="max-w-[460px]">
            <DialogHeader>
              <DialogTitle>
                {t('reminders.settings.askTitle', 'Turn on reminders for {{claimNounPluralLower}}?')}
              </DialogTitle>
              <DialogDescription>
                {t('reminders.settings.askBody', {
                  count: waiting,
                  defaultValue_one:
                    '{{count}} {{claimNounLower}} is already waiting on documents. Should it get reminders too?',
                  defaultValue_other:
                    '{{count}} {{claimNounPluralLower}} are already waiting on documents. Should they get reminders too?',
                })}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <ScopeChoice
                testId="reminders-only-new"
                title={t('reminders.settings.onlyNew', 'Only new requests')}
                hint={t('reminders.settings.onlyNewHint', {
                  count: waiting,
                  defaultValue_one:
                    'Reminders start with the next documents you ask for. Nothing goes to the one already waiting.',
                  defaultValue_other:
                    'Reminders start with the next documents you ask for. Nothing goes to the {{count}} already waiting.',
                })}
                onClick={() => answer(false)}
              />
              <ScopeChoice
                testId="reminders-include-open"
                title={t('reminders.settings.includeOpen', {
                  count: waiting,
                  defaultValue_one: 'Include the open request',
                  defaultValue_other: 'Include the {{count}} open requests',
                })}
                hint={t(
                  'reminders.settings.includeOpenHint',
                  'Each gets its first reminder within the hour, then one on your schedule.',
                )}
                onClick={() => answer(true)}
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => answer(null)}>
                {t('reminders.settings.keepOff', 'Keep them off')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {closing && (
        <Dialog open onOpenChange={(open) => !open && answerClosing(false)}>
          <DialogContent className="max-w-[460px]">
            <DialogHeader>
              <DialogTitle>
                {t('reminders.settings.closeTitle', {
                  count: closing.count,
                  defaultValue_one: 'Close {{count}} {{claimNounLower}}?',
                  defaultValue_other: 'Close {{count}} {{claimNounPluralLower}}?',
                })}
              </DialogTitle>
              <DialogDescription>
                {t('reminders.settings.closeBody', {
                  count: closing.count,
                  perRun: closing.perRun,
                  defaultValue_one:
                    '{{count}} {{claimNounLower}} has had every reminder and nothing has arrived. Saving closes it at the next hourly run.',
                  defaultValue_other:
                    '{{count}} {{claimNounPluralLower}} have had every reminder and nothing has arrived. Saving closes them over the next hourly runs, up to {{perRun}} at a time.',
                })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => answerClosing(false)}>
                {t('reminders.settings.closeCancel', "Don't save")}
              </Button>
              <Button
                variant="destructive"
                onClick={() => answerClosing(true)}
                data-testid="reminders-close-confirm"
              >
                {t('reminders.settings.closeConfirm', {
                  count: closing.count,
                  defaultValue_one: 'Save and close it',
                  defaultValue_other: 'Save and close them',
                })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

type T = ReturnType<typeof useTranslation>['t']

function sendLine(
  ctx: FormContext,
  t: T,
  claims: ReminderSettings['claims'] | undefined,
  waiting: number,
): string {
  const v = ctx.values
  if (!v.send_reminders) {
    return t(
      'reminders.settings.sendOff',
      'Off. Customers are not reminded about documents they still owe.',
    )
  }
  const every = toNullableInt(v.interval_days)
  const upTo = toNullableInt(v.max_reminders)
  let line: string
  if (!every || !upTo) {
    line = t(
      'reminders.settings.sendOnDefault',
      'Customers who still owe documents get an email on the default schedule.',
    )
  } else if (upTo === 1) {
    line = t('reminders.settings.sendOnce', 'Customers who still owe documents get one email {{after}}.', {
      after: t('reminders.settings.afterDays', {
        count: every,
        defaultValue_one: 'after a day',
        defaultValue_other: 'after {{count}} days',
      }),
    })
  } else {
    line = t(
      'reminders.settings.sendOn',
      'Customers who still owe documents get an email {{every}}, up to {{max}} times.',
      {
        every: t('reminders.settings.everyDays', {
          count: every,
          defaultValue_one: 'every day',
          defaultValue_other: 'every {{count}} days',
        }),
        max: upTo,
      },
    )
  }
  // Only while turning reminders on: who the change reaches.
  if (claims?.send_reminders || waiting === 0) return line
  const scope = v.apply_to_open
    ? t('reminders.settings.scopeAll', {
        count: waiting,
        defaultValue_one: 'The request already open gets them too.',
        defaultValue_other: 'The {{count}} requests already open get them too.',
      })
    : t('reminders.settings.scopeNew', 'Requests already open are left as they are.')
  return `${line} ${scope}`
}

function closeLine(ctx: FormContext, t: T): string {
  if (!ctx.values.auto_close) {
    return t(
      'reminders.settings.closeOff',
      'Off. Reminders stop after the last one and the {{claimNounLower}} stays open.',
    )
  }
  const after = toNullableInt(ctx.values.close_after_days)
  if (!after) {
    return t(
      'reminders.settings.closeOnDefault',
      'Closes it one reminder interval after the last reminder.',
    )
  }
  return t('reminders.settings.closeOn', {
    count: after,
    defaultValue_one: 'Closes it a day after the last reminder.',
    defaultValue_other: 'Closes it {{count}} days after the last reminder.',
  })
}

function ClaimsCardFoot({
  formContext,
  data,
  emailTemplatesOn,
  emailTriggersOn,
}: {
  formContext: FormContext
  data: ReminderSettings
  emailTemplatesOn: boolean
  emailTriggersOn: boolean
}) {
  const { t } = useTranslation()
  const on = Boolean(formContext.values.send_reminders)
  const closing = on && Boolean(formContext.values.auto_close)
  const names = [
    data.emails.reminder,
    closing && formContext.values.closure_email ? data.emails.closure : null,
  ].filter((name): name is string => Boolean(name))
  const needsSetup =
    !data.setup.has_document_request || !data.setup.sends_document_requests
  const { waiting, due_today: due, closing_7d: closingSoon } = data.summary

  return (
    <>
      {on && names.length > 0 && (
        <EmailsUsed names={names} editable={emailTemplatesOn} className="-mt-1 px-5 pb-5" />
      )}
      {closing && data.setup.reminder_has_final_warning === false && (
        <div
          className="mx-5 mb-5 flex items-start gap-2 rounded-[4px] border-[0.5px] border-[var(--status-warn-bg)] bg-[var(--status-warn-bg)] px-3 py-2 text-[12px] text-[var(--status-warn)]"
          data-testid="reminders-final-warning-missing"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {t(
              'reminders.settings.finalWarningMissing',
              'The reminder email has no final warning, so customers are not told their {{claimNounLower}} will close. Add it back in the wording.',
            )}{' '}
            {emailTemplatesOn && (
              <Link
                to="/settings/email-templates"
                className="underline underline-offset-[3px]"
              >
                {t('reminders.settings.editWording', 'Edit the wording')}
              </Link>
            )}
          </span>
        </div>
      )}
      {needsSetup && (
        <SetupGuide
          setup={data.setup}
          remindersOn={on}
          emailTemplatesOn={emailTemplatesOn}
          emailTriggersOn={emailTriggersOn}
        />
      )}
      <div
        className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 border-t-[0.5px] border-[var(--rule)] px-5 py-3 text-[12px] text-[var(--ink-3)]"
        data-testid="reminders-summary"
      >
        {on ? (
          <span>
            <span className="font-normal text-[var(--ink)]">{waiting}</span>{' '}
            {t('reminders.settings.waitingOnDocs', 'waiting on documents')}
            <span className="mx-1.5 opacity-35">&middot;</span>
            <span className="font-normal text-[var(--ink)]">{due}</span>{' '}
            {t('reminders.settings.dueToday', {
              count: due,
              defaultValue_one: 'reminder due today',
              defaultValue_other: 'reminders due today',
            })}
            {closing && (
              <>
                <span className="mx-1.5 opacity-35">&middot;</span>
                <span className="font-normal text-[var(--ink)]">{closingSoon}</span>{' '}
                {t('reminders.settings.closingThisWeek', 'closing this week')}
              </>
            )}
          </span>
        ) : (
          <span>
            {waiting > 0
              ? t('reminders.settings.waitingWhileOff', {
                  count: waiting,
                  defaultValue_one:
                    '{{count}} {{claimNounLower}} is waiting on documents. It gets no reminders while this is off.',
                  defaultValue_other:
                    '{{count}} {{claimNounPluralLower}} are waiting on documents. None of them get reminders while this is off.',
                })
              : t('reminders.settings.noneWaiting', 'Nothing is waiting on documents.')}
          </span>
        )}
        {waiting > 0 && (
          <Link
            to="/claims?reminders=waiting"
            className="inline-flex items-center gap-1 text-[var(--green)] hover:text-[var(--green-hover)]"
          >
            {t('reminders.settings.viewClaims', 'View {{claimNounPluralLower}}')}
            <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>
    </>
  )
}

function EmailsUsed({
  names,
  editable,
  className,
}: {
  names: string[]
  editable: boolean
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <p className={`text-[12px] text-[var(--ink-3)] ${className ?? ''}`}>
      {t('reminders.settings.emailsUsed', 'Emails used:')}{' '}
      <span className="text-[var(--ink-2)]">{names.join(', ')}</span>.{' '}
      {editable && (
        <Link
          to="/settings/email-templates"
          className="text-[var(--green)] underline underline-offset-[3px] hover:text-[var(--green-hover)]"
        >
          {t('reminders.settings.editWording', 'Edit the wording')}
        </Link>
      )}
    </p>
  )
}

function SetupGuide({
  setup,
  remindersOn,
  emailTemplatesOn,
  emailTriggersOn,
}: {
  setup: ReminderSettings['setup']
  remindersOn: boolean
  emailTemplatesOn: boolean
  emailTriggersOn: boolean
}) {
  const { t } = useTranslation()
  const steps = [
    {
      key: 'request',
      done: setup.has_document_request,
      title: t('reminders.settings.setupRequest', 'Create a document request'),
      body: t(
        'reminders.settings.setupRequestBody',
        'The email that asks a customer for documents, with a link to upload them.',
      ),
      link: emailTemplatesOn
        ? {
            to: '/settings/email-templates/new?kind=evidence_request',
            label: t('reminders.settings.setupRequestLink', 'Create one in Email Templates'),
          }
        : null,
    },
    {
      key: 'send',
      done: setup.sends_document_requests,
      title: t('reminders.settings.setupSend', 'Send it on your {{claimNounPluralLower}}'),
      body: t(
        'reminders.settings.setupSendBody',
        'By hand from a {{claimNounLower}}, or automatically when a {{claimNounLower}} reaches a step that needs documents.',
      ),
      link: emailTriggersOn
        ? {
            to: '/settings/email-triggers',
            label: t('reminders.settings.setupSendLink', 'Set it up in Email Triggers'),
          }
        : null,
    },
    {
      key: 'on',
      done: remindersOn,
      title: t('reminders.settings.setupOn', 'Turn on reminders'),
      body: t(
        'reminders.settings.setupOnBody',
        'Use the Send reminders switch. You can do this first: reminders start on their own once a {{claimNounLower}} has an open request.',
      ),
      link: null,
    },
  ]
  return (
    <div className="border-t-[0.5px] border-[var(--rule)] p-5" data-testid="reminders-setup-guide">
      <p className="mb-1 text-[13px] font-normal leading-[1.4] text-[var(--ink)]">
        {t('reminders.settings.setupTitle', 'Before the first reminder can go out')}
      </p>
      <p className="mb-4 text-[12px] leading-normal text-[var(--ink-3)]">
        {t(
          'reminders.settings.setupBody',
          'Reminders follow up on documents you have asked a customer for. Nothing on your account asks for documents yet.',
        )}
      </p>
      <ol className="flex flex-col gap-3.5">
        {steps.map((step) => (
          <li key={step.key} className="flex items-start gap-2.5">
            <span
              className={`flex h-5 w-4 shrink-0 items-center ${step.done ? 'text-[var(--green)]' : 'text-[var(--ink-3)]'}`}
            >
              {step.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
            </span>
            <span className="min-w-0">
              <span className="block text-[13px] leading-normal text-[var(--ink)]">{step.title}</span>
              <span className="block text-[12px] leading-normal text-[var(--ink-3)]">{step.body}</span>
              {step.link && !step.done && (
                <Link
                  to={step.link.to}
                  className="mt-0.5 inline-flex items-center gap-1 text-[12px] text-[var(--green)] hover:text-[var(--green-hover)]"
                >
                  {step.link.label}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              )}
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}

function ScopeChoice({
  title,
  hint,
  testId,
  onClick,
}: {
  title: string
  hint: string
  testId: string
  onClick: () => void
}) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      data-testid={testId}
      className="h-auto w-full flex-col items-start gap-0.5 whitespace-normal px-4 py-3 text-left"
    >
      <span className="text-[13px] font-normal text-[var(--ink)]">{title}</span>
      <span className="text-[12px] font-light text-[var(--ink-3)]">{hint}</span>
    </Button>
  )
}
